import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { FileText, ArrowLeft, Download, Copy, Check, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const BizPlanView = () => {
  const { planId } = useParams();
  const navigate = useNavigate();
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetchPlan();
  }, [planId]);

  const fetchPlan = async () => {
    try {
      const token = localStorage.getItem('careerboost_token');
      const response = await axios.get(
        `${BACKEND_URL}/api/bizplan/plans/${planId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPlan(response.data);
    } catch (error) {
      console.debug('Error fetching plan:', error);
      toast.error('Failed to load plan');
      navigate('/bizplanai/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(plan.content);
      setCopied(true);
      toast.success('Plan copied to clipboard!');
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast.error('Failed to copy');
    }
  };

  const handleDownload = () => {
    const element = document.createElement('a');
    const file = new Blob([plan.content], { type: 'text/plain' });
    element.href = URL.createObjectURL(file);
    element.download = `${plan.business_name.replace(/\s+/g, '_')}_Business_Plan.txt`;
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
    toast.success('Plan downloaded!');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  if (!plan) {
    return null;
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Button variant="ghost" onClick={() => navigate('/bizplanai/dashboard')} data-testid="back-btn">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-emerald-600 rounded-lg flex items-center justify-center">
                  <FileText className="w-5 h-5 text-white" />
                </div>
                <span className="font-bold text-lg text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  BizPlanAI
                </span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={handleCopy} data-testid="copy-btn">
                {copied ? (
                  <>
                    <Check className="w-4 h-4 mr-2 text-green-500" />
                    Copied!
                  </>
                ) : (
                  <>
                    <Copy className="w-4 h-4 mr-2" />
                    Copy
                  </>
                )}
              </Button>
              <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={handleDownload} data-testid="download-btn">
                <Download className="w-4 h-4 mr-2" />
                Download
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Plan Content */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Plan Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {plan.business_name}
            </h1>
            <span className={`text-sm px-3 py-1 rounded-full ${
              plan.plan_type === 'detailed' 
                ? 'bg-purple-100 text-purple-700' 
                : 'bg-slate-100 text-slate-700'
            }`}>
              {plan.plan_type === 'detailed' ? 'Detailed Plan' : 'Basic Plan'}
            </span>
          </div>
          <p className="text-slate-600">{plan.industry}</p>
        </div>

        {/* Plan Document */}
        <Card className="shadow-lg">
          <CardContent className="p-8 md:p-12">
            <div 
              className="prose prose-slate max-w-none"
              style={{ 
                whiteSpace: 'pre-wrap',
                lineHeight: '1.8',
                fontSize: '15px'
              }}
              data-testid="plan-content"
            >
              {plan.content}
            </div>
          </CardContent>
        </Card>

        {/* Actions */}
        <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
          <Link to="/bizplanai/generate">
            <Button variant="outline" className="w-full sm:w-auto" data-testid="create-another-btn">
              Create Another Plan
            </Button>
          </Link>
          <Button className="bg-emerald-600 hover:bg-emerald-700 w-full sm:w-auto" onClick={handleDownload}>
            <Download className="w-4 h-4 mr-2" />
            Download Plan
          </Button>
        </div>
      </div>
    </div>
  );
};

export default BizPlanView;
